/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
// we need to use tabindex on popup ^^ so it could be interactable right away
// when it's appears
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ScrollLock from 'react-scrolllock';
import Dropdown from 'react-dropdown';
import classNames from 'classnames';
import {
    GENRE_TYPES,
    CS_MEASURES,
    DEC_MEASURES,
    SKILL_DISPLAY_NAMES,
    MEASURE_SCORE_DISPLAY_NAMES,
    MEASURE_BY_MEASURE_SCORE,
} from '../../constants/assessments';
import Button from '../buttons/Button';
import RadioButton from '../buttons/RadioButton';
import { getMeasureScoreBySkillName } from '../utils/measureScoreBySkillName';

import { getFocusableElementsList, handleTabKeyPress } from './utils';

import { openOnEnterKeyPressedOnDropdown } from '../utils/dropDownHelpers';

// this Map is used to store ref for all dropdowns for each student in PM view
const refCollection = new Map();

const getFormSelectionContent = (
    measure,
    gradeOptions,
    getSelectedGradeOption,
    setSelectedGrade,
    formOptions,
    getSelectedFormOption,
    setSelectedForm,
    selectedGenre,
    setSelectedGenre,
) =>
    CS_MEASURES.includes(measure) || DEC_MEASURES.includes(MEASURE_BY_MEASURE_SCORE[measure]) ? (
        <>
            {CS_MEASURES.includes(measure) ? (
                <>
                    <RadioButton
                        id={GENRE_TYPES.FICTION}
                        key={GENRE_TYPES.FICTION}
                        value={GENRE_TYPES.FICTION}
                        name="Fiction"
                        isChecked={selectedGenre === GENRE_TYPES.FICTION}
                        onChange={e => setSelectedGenre(e.currentTarget.value)}
                    />
                    <RadioButton
                        id={GENRE_TYPES.NONFICTION}
                        key={GENRE_TYPES.NONFICTION}
                        value={GENRE_TYPES.NONFICTION}
                        name="Nonfiction"
                        isChecked={selectedGenre === GENRE_TYPES.NONFICTION}
                        onChange={e => setSelectedGenre(e.currentTarget.value)}
                    />
                </>
            ) : (
                <div className='dashboard-measure-popup-body-text'>Select a form:</div>
            )}
            <div className='dashboard-form-selection-dropdown-wrapper' tabIndex={0}>
                <Dropdown
                    controlClassName='dashboard-form-selection-dropdown'
                    arrowClassName='dashboard-form-selection-dropdown-arrow'
                    menuClassName='dashboard-form-selection-dropdown-menu'
                    options={formOptions}
                    value={getSelectedFormOption}
                    onChange={e => setSelectedForm(e.value)}
                    ref={refCollection.get('form-selection-dropdown')}
                />
            </div>
        </>
    ) : (
        <>
            <div className='dashboard-grade-selection-dropdown-wrapper' tabIndex={0}>
                <Dropdown
                    controlClassName='dashboard-grade-selection-dropdown'
                    arrowClassName='dashboard-grade-selection-dropdown-arrow'
                    menuClassName='dashboard-grade-selection-dropdown-menu'
                    options={gradeOptions}
                    value={getSelectedGradeOption}
                    onChange={e => setSelectedGrade(e.value)}
                    ref={refCollection.get('grade-selection-dropdown')}
                />
            </div>
            <div className='dashboard-form-selection-dropdown-wrapper' tabIndex={0}>
                <Dropdown
                    controlClassName='dashboard-form-selection-dropdown'
                    arrowClassName='dashboard-form-selection-dropdown-arrow'
                    menuClassName='dashboard-form-selection-dropdown-menu'
                    options={formOptions}
                    value={getSelectedFormOption}
                    placeholder="Select form"
                    onChange={e => setSelectedForm(e.value)}
                    ref={refCollection.get('form-selection-dropdown')}
                />
            </div>
        </>
    );

const FormSelectionPopup = ({
    gradeOptions,
    isPopupOpened,
    onClose,
    isScrollLockEnabled,
    getSelectedGradeOption,
    setSelectedGrade,
    formOptions,
    getSelectedFormOption,
    setSelectedForm,
    selectedGenre,
    setSelectedGenre,
    skillName,
    measureName,
    onClick,
}) => {
    const popupClassNames = classNames('dashboard-measure-popup', 'dashboard-measure-popup-backdrop', {
        'dashboard-opened': isPopupOpened,
    });

    const measureScoreName = getMeasureScoreBySkillName(skillName, measureName);

    refCollection.set('grade-selection-dropdown', useRef(null));
    refCollection.set('form-selection-dropdown', useRef(null));

    const modalRef = useRef(null);

    useEffect(() => {
        const modalElement = modalRef.current;
        // apply focus to popup in order to operate popup buttons
        modalElement.focus();

        const [firstElement, lastElement] = getFocusableElementsList(modalElement);

        modalElement.addEventListener('keydown', handleTabKeyPress(firstElement, lastElement));

        const isDevEnv = process.env.NODE_ENV === 'development';
        const dashboardPrefix = isDevEnv ? '' : 'dashboard-';

        const gradeSelectionDropdownSelector = `.${dashboardPrefix}grade-selection-dropdown-wrapper`;
        const formSelectionDropdownSelector = `.${dashboardPrefix}form-selection-dropdown-wrapper`;

        const gradeSelectionDropdown = document.querySelector(gradeSelectionDropdownSelector);
        const formSelectionDropdown = document.querySelector(formSelectionDropdownSelector);

        if (gradeSelectionDropdown) {
            gradeSelectionDropdown.addEventListener(
                'keydown',
                openOnEnterKeyPressedOnDropdown(
                    refCollection.get('grade-selection-dropdown'),
                    gradeSelectionDropdown,
                ),
            );
        }

        if (formSelectionDropdown) {
            formSelectionDropdown.addEventListener(
                'keydown',
                openOnEnterKeyPressedOnDropdown(
                    refCollection.get('form-selection-dropdown'),
                    formSelectionDropdown,
                    true,
                ),
            );
        }

        return () => {
            if (gradeSelectionDropdown) {
                gradeSelectionDropdown.removeEventListener(
                    'keydown',
                    openOnEnterKeyPressedOnDropdown(
                        refCollection.get('grade-selection-dropdown'),
                        gradeSelectionDropdown,
                    ),
                );
            }

            if (formSelectionDropdown) {
                formSelectionDropdown.removeEventListener(
                    'keydown',
                    openOnEnterKeyPressedOnDropdown(
                        refCollection.get('form-selection-dropdown'),
                        formSelectionDropdown,
                        true,
                    ),
                );
            }

            modalElement.removeEventListener(
                'keydown',
                handleTabKeyPress(firstElement, lastElement),
            );
        };
    }, []);

    return (
        <div className={popupClassNames}>
            <ScrollLock isActive={isScrollLockEnabled}>
                <div className='dashboard-measure-popup-wrapper' tabIndex={0} ref={modalRef}>
                    <div className='dashboard-measure-popup-header'>
                        <p>
                            {SKILL_DISPLAY_NAMES[skillName]}
                            <span className='dashboard-light'>
                                {MEASURE_SCORE_DISPLAY_NAMES[measureScoreName]}
                            </span>
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className='dashboard-close-button'
                            tabIndex={0}
                        >
                            &#x2715;
                        </button>
                    </div>
                    <div className='dashboard-measure-popup-body'>
                        {getFormSelectionContent(
                            measureScoreName,
                            gradeOptions,
                            getSelectedGradeOption,
                            setSelectedGrade,
                            formOptions,
                            getSelectedFormOption,
                            setSelectedForm,
                            selectedGenre,
                            setSelectedGenre,
                        )}
                        <Button
                            onClick={e => {
                                onClose();
                                onClick(e);
                            }}
                            tabIndex={0}
                        />
                    </div>
                </div>
            </ScrollLock>
        </div>
    );
};

FormSelectionPopup.propTypes = {
    gradeOptions: PropTypes.array.isRequired,
    isPopupOpened: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    isScrollLockEnabled: PropTypes.bool.isRequired,
    getSelectedGradeOption: PropTypes.object.isRequired,
    setSelectedGrade: PropTypes.func.isRequired,
    formOptions: PropTypes.array.isRequired,
    getSelectedFormOption: PropTypes.object,
    setSelectedForm: PropTypes.func.isRequired,
    selectedGenre: PropTypes.string,
    setSelectedGenre: PropTypes.func.isRequired,
    skillName: PropTypes.string.isRequired,
    measureName: PropTypes.string,
    onClick: PropTypes.func.isRequired,
};

FormSelectionPopup.defaultProps = {
    getSelectedFormOption: {},
    selectedGenre: undefined,
    measureName: undefined,
};

export default FormSelectionPopup;
